import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CoordinateInput } from '../../Common/Components/CoordinateInput';
import { Divider, Grid, ListItemText, MenuItem, Typography } from '@mui/material';
import {
  CompartmentSelector,
  CompartmentSelectorIndex,
  DevicePool,
  SortingStrategy,
  SortingStrategyType,
  Template,
  TemplateFilter,
  TemplateFilterType,
} from '@ekkogmbh/apisdk';
import { StyledTextField } from 'src/Common/Components/Forms/StyledTextField';
import { CompartmentSelectorPicker } from 'src/CompartmentSelectorManagement/CompartmentSelectorPicker';
import { TemplatePicker } from 'src/TemplateManagement/TemplatePicker';
import { StyledSelectField } from 'src/Common/Components/Forms/StyledSelectField';
import { DevicePoolSorting } from './DevicePoolSorting';
import { ContentPanel, StepDefinition, usePanel } from 'src/Common/Components/ContentControl/ContentPanel';
import NamedCoordinateDescription from 'src/Common/Components/Stepper/NameCoordinateDescription';
import useEkko from 'src/Common/Providers/EkkoProvider';
import useRequest from 'src/Common/Hooks/useRequest';
import { ReactivePanelColumn } from 'src/Common/Components/Stacks';
import usePagination from 'src/Common/Providers/PaginationProvider';
import { Settings } from '@mui/icons-material';

interface Props {
  devicePool?: DevicePool;
  closeHandler: () => void;
}

export const DevicePoolPanel = ({ devicePool, closeHandler }: Props): React.JSX.Element => {
  const { api } = useEkko();
  const { refresh } = usePagination();
  const { currentStep, enableNext, enableSave } = usePanel();

  const [isTemplateConfigOpen, setTemplateConfigOpen] = useState<boolean>(false);
  const [isSortingConfigOpen, setSortingConfigOpen] = useState<boolean>(false);

  const [, startLoading] = useRequest();
  const isEditMode = useMemo(() => devicePool !== undefined, [devicePool]);

  const [name, setName] = useState<string>('');
  const [coordinate, setCoordinate] = useState<string>('');
  const [compartmentSelector, setSelector] = useState<CompartmentSelectorIndex>();
  const [templateFilter, setTemplateFilter] = useState<TemplateFilter>({
    type: TemplateFilterType.NULL,
    templateNames: [],
  });
  const [sortingStrategy, setSortingStrategy] = useState<SortingStrategy>({
    name: SortingStrategyType.NULL,
    metadata: {},
  });

  const savePayload = useMemo(() => {
    if (['name', 'coordinate'].includes('') || compartmentSelector === undefined) {
      return undefined;
    }

    return {
      name,
      coordinate,
      compartmentSelector: {
        name: compartmentSelector.name,
        coordinate: compartmentSelector.coordinate,
      },
      templateFilter,
      sortingStrategy,
    } as DevicePool;
  }, [name, coordinate, compartmentSelector, templateFilter, sortingStrategy]);

  const save = useCallback(() => {
    if (savePayload == undefined) {
      return;
    }

    startLoading(
      () => api.saveDevicePool(savePayload, isEditMode),
      () => {
        refresh();
        closeHandler();
      },
    );
  }, [api, savePayload, isEditMode]);

  const reset = useCallback(() => {
    setName(devicePool?.name ?? '');
    setCoordinate(devicePool?.coordinate ?? '');
    setSelector(devicePool?.compartmentSelector);
    setTemplateFilter(
      devicePool?.templateFilter ?? {
        type: TemplateFilterType.NULL,
        templateNames: [],
      },
    );

    // PHP keeps turning empty objects into arrays
    const sortStrat = devicePool?.sortingStrategy ?? {
      name: SortingStrategyType.NULL,
      metadata: {},
    };

    if (sortStrat.name === SortingStrategyType.NULL && Array.isArray(sortStrat.metadata)) {
      sortStrat.metadata = {};
    }

    setSortingStrategy(sortStrat);
  }, [devicePool]);

  useEffect(() => {
    reset();
  }, [reset]);

  useEffect(() => {
    enableSave(savePayload !== undefined);

    switch (currentStep) {
      case 0:
        enableNext(![name, coordinate].includes(''));
        break;
      case 1:
        enableNext(compartmentSelector !== undefined);
        break;
      default:
        enableNext(true);
    }
  }, [currentStep, savePayload, name, coordinate, compartmentSelector]);

  const namingStep = useMemo(
    () => ({
      title: 'Device Pool',
      description: <NamedCoordinateDescription name={name} coordinate={coordinate} />,
      contentCallback: () => (
        <ReactivePanelColumn>
          <StyledTextField
            type={'text'}
            label={'Name'}
            value={name}
            onChange={(e) => {
              setName(String(e.target.value));
            }}
            disabled={isEditMode}
          />
          <CoordinateInput
            value={coordinate}
            onChange={(coordinate) => {
              setCoordinate(coordinate);
            }}
            trailingDelimiter={false}
            disabled={isEditMode}
          />
        </ReactivePanelColumn>
      ),
    }),
    [name, coordinate, isEditMode],
  );

  const templateFilterConfig = useMemo(
    () => (
      <StyledSelectField
        value={templateFilter.type}
        label={'Template Filter'}
        onChange={(e) => {
          const newType = e.target.value as TemplateFilterType;
          setTemplateFilter({
            ...templateFilter,
            type: newType,
          });

          if (![TemplateFilterType.NULL, templateFilter.type].includes(newType)) {
            setTemplateConfigOpen(true);
          } else if (newType === TemplateFilterType.NULL) {
            setTemplateConfigOpen(false);
          }
        }}
        action={{
          icon: Settings,
          onClick: () => setTemplateConfigOpen(!isTemplateConfigOpen),
          disabled: templateFilter.type === TemplateFilterType.NULL,
        }}
        collapsible={{
          open: isTemplateConfigOpen,
          content: (
            <TemplatePicker
              multiple
              coordinate={coordinate}
              selected={templateFilter.templateNames}
              onChangeCallback={(selection) => {
                const templateNames = (selection as (Template | string)[]).map((t) => {
                  if (typeof t === 'string') {
                    return t;
                  }
                  return t.name;
                });
                setTemplateFilter({
                  ...templateFilter,
                  templateNames,
                });
              }}
            />
          ),
        }}
      >
        {Object.values(TemplateFilterType).map((type: string) => (
          <MenuItem key={`template-filter-option-${type}`} value={type}>
            <ListItemText primary={type} />
          </MenuItem>
        ))}
      </StyledSelectField>
    ),
    [templateFilter, isTemplateConfigOpen],
  );

  const sortingConfig = useMemo(() => {
    const sortingOptions = Object.values(SortingStrategyType).map((name: string) => (
      <MenuItem key={`sorting-strategy-option-${name}`} value={name}>
        <ListItemText primary={name} />
      </MenuItem>
    ));

    let metadataForm: React.JSX.Element | undefined = undefined;
    switch (sortingStrategy.name) {
      case SortingStrategyType.VALUE_ALPHANUMERIC:
        metadataForm = (
          <StyledTextField
            type={'text'}
            label={'Value'}
            value={sortingStrategy.metadata.value !== undefined ? (sortingStrategy.metadata.value as string) : ''}
            tooltip={'leave empty to sort by coordinate'}
            onChange={(e) => {
              setSortingStrategy({
                ...sortingStrategy,
                metadata: { value: String(e.target.value) },
              });
            }}
          />
        );
        break;
      case SortingStrategyType.MANUAL:
        metadataForm = (
          <DevicePoolSorting
            coordinates={(sortingStrategy.metadata.coordinates ?? []) as string[]}
            onChange={(coordinates) => {
              setSortingStrategy({
                ...sortingStrategy,
                metadata: { coordinates },
              });
            }}
          />
        );
        break;
    }

    return (
      <StyledSelectField
        value={sortingStrategy.name}
        label={'Sorting Strategy'}
        onChange={(e) => {
          const sortType = String(e.target.value) as SortingStrategyType;

          let metadata = {};
          switch (sortType) {
            case SortingStrategyType.MANUAL:
              metadata = { coordinates: [] };
              break;
            case SortingStrategyType.VALUE_ALPHANUMERIC:
              metadata = { value: '' };
              break;
            case SortingStrategyType.NULL:
              break;
          }

          setSortingStrategy({
            name: sortType,
            metadata,
          });
          setSortingConfigOpen(sortType !== SortingStrategyType.NULL && sortingStrategy.name !== sortType);
        }}
        action={{
          icon: Settings,
          disabled: metadataForm === undefined,
          onClick: () => setSortingConfigOpen(!isSortingConfigOpen),
        }}
        collapsible={{
          open: isSortingConfigOpen,
          content: metadataForm ?? <></>,
        }}
      >
        {sortingOptions}
      </StyledSelectField>
    );
  }, [isSortingConfigOpen, sortingStrategy]);

  const configurationStep: StepDefinition = useMemo(
    () => ({
      title: 'Configuration',
      contentCallback: () => (
        <ReactivePanelColumn>
          <CompartmentSelectorPicker
            coordinate={coordinate}
            selected={compartmentSelector as CompartmentSelectorIndex}
            onChange={(newSelected: CompartmentSelector | undefined) => {
              setSelector(newSelected);
            }}
          />
          {templateFilterConfig}
          {sortingConfig}
        </ReactivePanelColumn>
      ),
    }),
    [coordinate, compartmentSelector, sortingConfig, templateFilterConfig],
  );

  const overviewStep = useMemo(
    () => ({
      title: 'Overview',
      contentCallback: () => (
        <Grid container item xs={10} spacing={2} alignItems={'stretch'} style={{ wordBreak: 'break-word' }}>
          <Grid container item xs={10}>
            <Grid item xs={12}>
              <Typography variant={'overline'} color={'primary'}>
                Identifier
              </Typography>
              <Divider />
            </Grid>
            <Grid item xs={3}>
              <Typography variant={'h6'} color={'primary'}>
                Name
              </Typography>
              <Typography variant={'h5'}>{name}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant={'h6'} color={'primary'}>
                Coordinate
              </Typography>
              <Typography variant={'h5'}>{coordinate}</Typography>
            </Grid>
          </Grid>
          <Grid container item xs={10}>
            <Grid item xs={12}>
              <Typography variant={'overline'} color={'primary'}>
                Compartment Selector
              </Typography>
              <Divider />
            </Grid>
            <Grid item xs={3}>
              <Typography variant={'h6'} color={'primary'}>
                Name
              </Typography>
              <Typography variant={'h5'}>{compartmentSelector?.name}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant={'h6'} color={'primary'}>
                Coordinate
              </Typography>
              <Typography variant={'h5'}>{compartmentSelector?.coordinate}</Typography>
            </Grid>
          </Grid>
          <Grid container item xs={10}>
            <Grid item xs={12}>
              <Typography variant={'overline'} color={'primary'}>
                Template Filter
              </Typography>
              <Divider />
            </Grid>
            <Grid item xs={3}>
              <Typography variant={'h6'} color={'primary'}>
                Type
              </Typography>
              <Typography variant={'h5'}>{templateFilter.type}</Typography>
            </Grid>
            {templateFilter.templateNames.length > 0 && (
              <Grid item xs={9}>
                <Typography variant={'h6'} color={'primary'}>
                  Templates
                </Typography>
                <Typography variant={'h5'}>{templateFilter.templateNames.join(', ')}</Typography>
              </Grid>
            )}
          </Grid>
          <Grid container item xs={10}>
            <Grid item xs={12}>
              <Typography variant={'overline'} color={'primary'}>
                Sorting Strategy
              </Typography>
              <Divider />
            </Grid>
            <Grid item xs={3}>
              <Typography variant={'h6'} color={'primary'}>
                Name
              </Typography>
              <Typography variant={'h5'}>{sortingStrategy.name}</Typography>
            </Grid>
          </Grid>
        </Grid>
      ),
    }),
    [name, coordinate, compartmentSelector, templateFilter, sortingStrategy],
  );

  return (
    <ContentPanel
      steps={[namingStep, configurationStep, overviewStep]}
      initialStep={isEditMode ? 1 : 0}
      onReset={reset}
      onCancel={closeHandler}
      onSave={save}
    />
  );
};
